import React, { useState, useEffect, useCallback, useRef } from 'react';
import { transactionService } from '../../services/transactionService';
import TodayReceiptsModal from './TodayReceiptsModal';

const STATUS_META = {
  F:   { icon: '✓', color: 'var(--success)',    label: 'Zafiskalizowany',                       short: 'OK',     rowBg: 'transparent' },
  'F!':{ icon: '✗', color: 'var(--danger)',     label: 'Błąd fiskalizacji',                     short: 'Blad',   rowBg: 'rgba(220, 53, 69, 0.18)' },
  NF:  { icon: '?', color: 'var(--warning)',    label: 'Niezafiskalizowany - wymaga fiskalizacji', short: 'Brak',  rowBg: 'rgba(255, 193, 7, 0.18)' },
  ND:  { icon: '–', color: 'var(--text-muted)', label: 'Nie dotyczy (korekta)',                 short: 'Korekta',rowBg: 'transparent' },
};

// PAR/0016/04/2026/KAL -> "0016"  (sequence number, krotszy niz pelny numer)
const shortNumber = (receiptNumber, fallbackId) => {
  if (!receiptNumber) return fallbackId ? `#${fallbackId}` : '';
  const parts = String(receiptNumber).split('/');
  if (parts.length >= 2 && /^\d+$/.test(parts[1])) return `#${parts[1]}`;
  // korekta lub inny format - obetnij do 10 znakow
  return receiptNumber.length > 10 ? `${receiptNumber.slice(0, 9)}…` : receiptNumber;
};

const formatTime = (raw) => {
  if (!raw) return '';
  if (typeof raw === 'string' && raw.length >= 5) return raw.substring(0, 5);
  return '';
};

const RecentReceiptsWidget = ({ refreshTrigger, locationId, onClickReceipt }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const loadingRef = useRef(false); // aktualny stan ladowania dla intervalu (bez stale closure)

  const load = useCallback(async () => {
    loadingRef.current = true;
    setLoading(true);
    setError(null);
    try {
      const filters = { limit: 10, status: 'completed' };
      if (locationId) filters.location_id = locationId;
      const res = await transactionService.getTransactions(filters);
      const list = res?.data?.transactions || res?.transactions || [];
      setItems(list);
    } catch (e) {
      setError('Błąd');
    } finally {
      loadingRef.current = false;
      setLoading(false);
    }
  }, [locationId]);

  useEffect(() => { load(); }, [load, refreshTrigger]);

  // Wewnetrzny bezpieczny interval odswiezania (~15s) - dogania liste, gdy
  // zewnetrzny refreshTrigger zostal zgubiony. Zewnetrzny trigger zostaje niezalezny.
  useEffect(() => {
    const REFRESH_MS = 15000;
    const tick = () => {
      // Nie odswiezaj gdy karta w tle (oszczedza requesty) ani gdy trwa juz load
      if (document.visibilityState !== 'visible') return;
      if (loadingRef.current) return;
      load();
    };
    const id = setInterval(tick, REFRESH_MS);
    return () => clearInterval(id);
  }, [load]);

  // Licznik statusow do badge w naglowku
  const counts = items.reduce((acc, tx) => {
    const s = tx.fiscal_status || 'F!';
    acc[s] = (acc[s] || 0) + 1;
    return acc;
  }, {});
  const hasIssues = (counts['F!'] || 0) + (counts['NF'] || 0) > 0;

  return (
    <div
      style={{
        backgroundColor: 'var(--card-bg)',
        borderLeft: `3px solid ${hasIssues ? 'var(--warning)' : 'var(--info)'}`,
        borderRadius: '4px',
        padding: '0.4rem 0.5rem',
        width: '170px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <div
        style={{
          fontSize: '11px',
          fontWeight: 600,
          color: hasIssues ? 'var(--warning)' : 'var(--info)',
          marginBottom: '4px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
          Paragony
          {hasIssues && (
            <span
              title={`Bledy: ${counts['F!'] || 0}, niezafiskalizowane: ${counts['NF'] || 0}`}
              style={{
                fontSize: '9px',
                background: 'var(--warning)',
                color: '#fff',
                borderRadius: '8px',
                padding: '0 5px',
                fontWeight: 700,
              }}
            >
              {(counts['F!'] || 0) + (counts['NF'] || 0)}
            </span>
          )}
        </span>
        <button
          onClick={load}
          disabled={loading}
          title="Odśwież"
          style={{
            border: 'none',
            background: 'transparent',
            color: 'var(--text-muted)',
            cursor: 'pointer',
            fontSize: '11px',
            padding: '0 2px',
            lineHeight: 1,
          }}
        >
          {loading ? '…' : '↻'}
        </button>
      </div>

      {error && (
        <div style={{ fontSize: '10px', color: 'var(--danger)' }}>{error}</div>
      )}
      {!error && items.length === 0 && !loading && (
        <div style={{ fontSize: '10px', color: 'var(--text-muted)' }}>Brak</div>
      )}

      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '1px',
          maxHeight: '48px',
          overflowY: 'auto',
          paddingRight: '2px',
        }}
      >
        {items.map((tx) => {
          const status = tx.fiscal_status || 'F!';
          const meta = STATUS_META[status] || STATUS_META['F!'];
          const total = (typeof tx.total_amount === 'number') ? tx.total_amount : parseFloat(tx.total_amount || 0);
          const shortNum = shortNumber(tx.receipt_number || tx.numer_paragonu, tx.id);
          const time = formatTime(tx.transaction_time || tx.czas_transakcji);
          const bladInfo = tx.fiskalizacja_blad ? `\n\nBlad: ${tx.fiskalizacja_blad}` : '';
          const fullTitle = `${meta.label}\n${tx.receipt_number || tx.numer_paragonu || `#${tx.id}`}${time ? '  ' + time : ''}\n${total.toFixed(2)} zl${bladInfo}\n\nAby fiskalizowac/poprawic kliknij "wiecej z dzisiaj" ponizej`;
          return (
            <div
              key={tx.id}
              style={{
                display: 'grid',
                gridTemplateColumns: '12px 1fr auto',
                gap: '4px',
                alignItems: 'center',
                fontSize: '10px',
                padding: '1px 3px',
                borderRadius: '3px',
                color: 'var(--text)',
                lineHeight: 1.3,
                backgroundColor: meta.rowBg,
              }}
              title={fullTitle}
            >
              <span style={{ color: meta.color, fontWeight: 700, textAlign: 'center' }}>{meta.icon}</span>
              <span style={{ fontFamily: 'monospace', color: 'var(--text-muted)' }}>
                {shortNum}
              </span>
              <span style={{ fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>
                {total.toFixed(2)}
              </span>
            </div>
          );
        })}
      </div>

      {/* "wiecej..." - link do modala z paragonami z dzisiaj */}
      <button
        onClick={() => setShowModal(true)}
        style={{
          marginTop: '3px',
          padding: '2px 0',
          border: 'none',
          background: 'transparent',
          color: 'var(--info)',
          fontSize: '11px',
          cursor: 'pointer',
          textAlign: 'center',
          fontWeight: 600,
        }}
      >
        więcej z dzisiaj →
      </button>

      {/* Legenda statusow - mala, kompaktowa */}
      {items.length > 0 && (
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            marginTop: '3px',
            paddingTop: '3px',
            borderTop: '1px solid var(--border, rgba(0,0,0,0.08))',
            fontSize: '9px',
            color: 'var(--text-muted)',
          }}
        >
          {['F', 'F!', 'NF', 'ND'].map((k) => {
            const m = STATUS_META[k];
            return (
              <span key={k} title={m.label} style={{ display: 'inline-flex', alignItems: 'center', gap: '2px' }}>
                <span style={{ color: m.color, fontWeight: 700 }}>{m.icon}</span>
                <span>{m.short}</span>
              </span>
            );
          })}
        </div>
      )}

      <TodayReceiptsModal
        isOpen={showModal}
        onClose={() => {
          setShowModal(false);
          load();  // odswiez widget gdy zamykasz modal (akcje w modal mogly zmienic status)
        }}
        locationId={locationId}
        onClickReceipt={onClickReceipt ? async (tx) => {
          // NIE zamykaj modal po kliknięciu - user moze chciec fiskalizowac wiele paragonow
          await onClickReceipt(tx);
          load();  // odswiez glowny widget od razu
        } : undefined}
      />
    </div>
  );
};

export default RecentReceiptsWidget;
